/**
 * users.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin users management — session-gated, full sidebar, design-matched to
 * AdminDashboard.
 * Route: /admin/users
 *
 * Tables used:
 *   users                (id, first_name, last_name, email, status, created_at)
 *   wallets              (user_id, balance)
 *   transactions         (user_id, type, amount, status, created_at)
 *   user_subscriptions   (user_id, plan_id, status, created_at → subscription_plans)
 *   subscription_plans   (id, name)
 *   open_positions       (user_id, status)
 *   stock_orders         (user_id, status)
 *   support_tickets      (user_id, status)
 *   notifications        (user_id, read)
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import {
  // sidebar icons
  LayoutDashboard, Wallet, Receipt, Copy, UserCheck,
  HeadphonesIcon, Bell, CandlestickChart, BarChart2, ShoppingBag,
  ChevronRight, LogOut,CreditCard, MapPin,
  // page icons
  Users, Search, RefreshCw, Eye, Trash2, X,
  CheckCircle2, AlertCircle, XCircle, Clock,
  ChevronUp, ChevronDown, ShieldCheck, ShieldOff,
  Activity, Plus,
  Mail, Calendar, Shield,
} from "lucide-react";

const PER_PAGE = 15;

// ─── Sidebar nav config (matches AdminDashboard exactly) ──────────────────────
const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      { to: "/admin",            label: "Dashboard",      icon: LayoutDashboard, end: true },
    ],
  },
  {
    label: "Users & Finance",
    items: [
      { to: "/admin/users",        label: "Users",        icon: Users   },
      { to: "/admin/wallets",      label: "Wallets",      icon: Wallet  },
      { to: "/admin/transactions", label: "Transactions", icon: Receipt },
    ],
  },
  {
    label: "Trading",
    items: [
      { to: "/admin/copy-trades",  label: "Copy trades",   icon: Copy             },
      { to: "/admin/traders",      label: "Hired traders", icon: UserCheck        },
      { to: "/admin/trading",      label: "Open trades",   icon: CandlestickChart },
      { to: "/admin/stock-trades", label: "Stock trades",  icon: BarChart2        },
      { to: "/admin/marketplace",  label: "Marketplace",   icon: ShoppingBag      },
    ],
  },
  {
    label: "Communication",
    items: [
      { to: "/admin/support",       label: "Support",       icon: HeadphonesIcon },
      { to: "/admin/notifications", label: "Notifications", icon: Bell           },
    ],
  },
  {
    label: "Settings",
    items: [
      { to: "/admin/subscriptions",      label: "Subscriptions",      icon: CreditCard },
      { to: "/admin/payment-addresses",  label: "Payment Addresses",  icon: MapPin     },
      { to: "/admin/market-subscriptions",  label: "Market Subscriptions",  icon: MapPin     },
    ],
  },
];

// ─── Injected styles ──────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg-base:      #06060f;
    --bg-surface:   #0a0a16;
    --bg-elevated:  #0e0e1c;
    --bg-card:      #111122;
    --bg-hover:     #14142a;
    --border:       rgba(255,255,255,0.055);
    --border-hi:    rgba(255,255,255,0.11);
    --border-glow:  rgba(99,102,241,0.3);

    --accent:       #6366f1;
    --accent-light: #818cf8;
    --accent-dim:   rgba(99,102,241,0.13);
    --accent-glow:  rgba(99,102,241,0.22);

    --green:        #34d399;
    --green-dim:    rgba(52,211,153,0.13);
    --amber:        #fbbf24;
    --amber-dim:    rgba(251,191,36,0.13);
    --rose:         #fb7185;
    --rose-dim:     rgba(251,113,133,0.13);
    --cyan:         #22d3ee;
    --cyan-dim:     rgba(34,211,238,0.13);
    --purple:       #a78bfa;
    --purple-dim:   rgba(167,139,250,0.13);
    --orange:       #fb923c;
    --orange-dim:   rgba(251,146,60,0.13);
    --blue:         #60a5fa;
    --blue-dim:     rgba(96,165,250,0.13);

    --text-primary:   #eeeeff;
    --text-secondary: #7878a0;
    --text-muted:     #3e3e58;

    --font-sans: 'Inter', system-ui, sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
    --sidebar-w: 232px;
    --topbar-h:  60px;
    --radius-sm: 8px;
    --radius-md: 14px;
    --radius-lg: 18px;
    --transition-smooth: cubic-bezier(0.4, 0, 0.2, 1);
  }

  html, body, #root {
    height: 100%; background: var(--bg-base);
    font-family: var(--font-sans); color-scheme: dark;
    scroll-behavior: smooth;
  }

  /* ── Noise texture overlay ── */
  .up-shell {
    display: flex; height: 100vh; overflow: hidden;
    background: var(--bg-base); position: relative;
  }
  .up-shell::before {
    content: '';
    position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
    opacity: 0.018;
  }

  /* ═══════════════════════════════════════
     SIDEBAR  (copied verbatim from ad-*)
  ════════════════════════════════════════ */
  .ad-sidebar {
    width: var(--sidebar-w);
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    background: var(--bg-surface);
    border-right: 1px solid var(--border);
    position: relative;
    z-index: 10;
    height: 100vh;
    overflow: hidden;
  }
  .ad-sidebar::after {
    content: '';
    position: absolute;
    top: 0; right: 0; bottom: 0; width: 1px;
    background: linear-gradient(to bottom, transparent 0%, var(--accent-glow) 35%, rgba(99,102,241,0.08) 70%, transparent 100%);
    pointer-events: none;
  }

  /* Logo */
  .ad-logo {
    display: flex; align-items: center; gap: 11px;
    padding: 0 18px;
    height: var(--topbar-h);
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
  }
  .ad-logo-icon {
    width: 32px; height: 32px; border-radius: 10px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 0 0 1px rgba(99,102,241,0.4), 0 4px 16px rgba(99,102,241,0.35), 0 0 32px rgba(99,102,241,0.15);
  }
  .ad-logo-text { line-height: 1; }
  .ad-logo-text strong {
    font-size: 13.5px; font-weight: 800;
    letter-spacing: -0.4px;
    background: linear-gradient(to right, #eeeeff, #a5b4fc);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .ad-logo-text span {
    font-size: 9.5px; color: var(--text-muted); display: block;
    margin-top: 3px; letter-spacing: 0.8px; text-transform: uppercase; font-weight: 500;
  }

  /* Nav */
  .ad-nav {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 18px 10px;
    display: flex;
    flex-direction: column;
    gap: 22px;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    min-height: 0; /* critical: lets flex child shrink below content height */
  }
  .ad-nav::-webkit-scrollbar { width: 3px; }
  .ad-nav::-webkit-scrollbar-track { background: transparent; }
  .ad-nav::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.2); border-radius: 99px; }
  .ad-nav::-webkit-scrollbar-thumb:hover { background: rgba(99,102,241,0.4); }
  .ad-nav-section-label {
    font-size: 9px; font-weight: 700; letter-spacing: 1.5px;
    text-transform: uppercase; color: var(--text-muted);
    padding: 0 10px; margin-bottom: 5px;
  }
  .ad-nav-items { display: flex; flex-direction: column; gap: 1px; }
  .ad-nav-link {
    display: flex; align-items: center; gap: 9px;
    padding: 8px 10px; border-radius: var(--radius-sm);
    font-size: 12.5px; font-weight: 500; color: var(--text-secondary);
    text-decoration: none; transition: all 0.18s ease;
    position: relative;
  }
  .ad-nav-link:hover { background: var(--bg-hover); color: var(--text-primary); }
  .ad-nav-link.active {
    background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.10) 100%);
    color: var(--accent-light);
    font-weight: 600;
    box-shadow: inset 0 0 0 1px rgba(99,102,241,0.15);
  }
  .ad-nav-link.active::before {
    content: '';
    position: absolute; left: 0; top: 18%; bottom: 18%;
    width: 2.5px; border-radius: 99px;
    background: linear-gradient(to bottom, var(--accent), var(--purple));
    box-shadow: 0 0 10px var(--accent-glow), 0 0 20px rgba(99,102,241,0.2);
  }
  .ad-nav-link svg { flex-shrink: 0; opacity: 0.65; transition: opacity 0.15s; }
  .ad-nav-link:hover svg, .ad-nav-link.active svg { opacity: 1; }
  .ad-nav-chevron { margin-left: auto; opacity: 0.35; }

  /* User card */
  .ad-user-card {
    flex-shrink: 0;
    border-top: 1px solid var(--border);
    padding: 12px 10px;
    background: linear-gradient(to top, rgba(99,102,241,0.04) 0%, transparent 100%);
  }
  .ad-user-inner {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 10px; border-radius: var(--radius-sm);
    transition: background 0.15s;
  }
  .ad-user-inner:hover { background: var(--bg-hover); }
  .ad-user-avatar {
    width: 32px; height: 32px; border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-dim), rgba(139,92,246,0.2));
    border: 1.5px solid rgba(99,102,241,0.35);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; color: var(--accent-light);
    flex-shrink: 0;
    box-shadow: 0 0 12px rgba(99,102,241,0.15);
  }
  .ad-user-info { flex: 1; min-width: 0; }
  .ad-user-info strong { font-size: 12px; font-weight: 600; color: var(--text-primary); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ad-user-info span   { font-size: 10px; color: var(--text-muted); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
  .ad-logout-btn {
    width: 28px; height: 28px; border-radius: 7px;
    background: none; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-muted); transition: all 0.15s;
  }
  .ad-logout-btn:hover { background: var(--rose-dim); color: var(--rose); box-shadow: 0 0 12px rgba(251,113,133,0.15); }

  /* ═══════════════════════════════════════
     PAGE COLUMN
  ════════════════════════════════════════ */
  .up-page { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; z-index: 1; }

  /* Topbar */
  .up-topbar {
    flex-shrink: 0;
    height: var(--topbar-h);
    background: rgba(10,10,22,0.85);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 28px;
    position: relative;
  }
  .up-topbar::after {
    content: '';
    position: absolute; bottom: -1px; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent 0%, var(--accent-glow) 50%, transparent 100%);
  }
  .up-topbar-left { display: flex; align-items: center; gap: 14px; }
  .up-topbar-badge {
    display: flex; align-items: center; gap: 6px;
    padding: 3px 10px; border-radius: 99px;
    background: var(--accent-dim); border: 1px solid rgba(99,102,241,0.2);
    font-size: 10px; font-weight: 700; color: var(--accent-light);
    letter-spacing: 0.5px; text-transform: uppercase;
  }
  .up-topbar-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.4px; }
  .up-topbar-sub   { font-size: 11px; color: var(--text-muted); margin-top: 1px; }
  .up-topbar-right { display: flex; align-items: center; gap: 10px; }

  /* Topbar buttons */
  .up-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 6px 14px; border-radius: var(--radius-sm);
    font-size: 11.5px; font-weight: 500;
    cursor: pointer; transition: all 0.18s;
    font-family: var(--font-sans); border: 1px solid var(--border-hi);
    background: var(--bg-card); color: var(--text-secondary);
  }
  .up-btn:hover {
    background: var(--accent-dim); color: var(--accent-light);
    border-color: rgba(99,102,241,0.3); box-shadow: 0 0 12px rgba(99,102,241,0.1);
  }
  .up-btn-primary {
    background: var(--accent); color: #fff; border-color: transparent; font-weight: 600;
  }
  .up-btn-primary:hover { background: #5254cc; border-color: transparent; box-shadow: 0 0 18px rgba(99,102,241,0.35); }

  /* Main scrollable */
  .up-main {
    flex: 1; overflow-y: auto;
    padding: 26px 28px;
    display: flex; flex-direction: column; gap: 22px;
  }
  .up-main::-webkit-scrollbar { width: 4px; }
  .up-main::-webkit-scrollbar-track { background: transparent; }
  .up-main::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.25); border-radius: 99px; }

  /* Stat cards */
  .up-stat-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 14px; }
  .up-stat {
    background: var(--bg-card); border: 1px solid var(--border);
    border-radius: var(--radius-md); padding: 20px;
    display: flex; flex-direction: column; gap: 14px;
    position: relative; overflow: hidden;
    transition: border-color 0.22s, box-shadow 0.22s, transform 0.18s;
  }
  .up-stat::before {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(ellipse at 110% -10%, var(--stat-glow, transparent) 0%, transparent 60%);
    pointer-events: none;
  }
  .up-stat::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent 20%, var(--stat-bar, transparent) 50%, transparent 80%);
    pointer-events: none;
  }
  .up-stat:hover { border-color: rgba(255,255,255,0.1); box-shadow: 0 4px 24px rgba(0,0,0,0.4); transform: translateY(-1px); }
  .up-stat-top { display: flex; align-items: flex-start; justify-content: space-between; }
  .up-stat-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase; color: var(--text-muted); }
  .up-stat-icon {
    width: 34px; height: 34px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; box-shadow: 0 2px 10px rgba(0,0,0,0.3);
  }
  .up-stat-value { font-size: 28px; font-weight: 700; color: var(--text-primary); letter-spacing: -1.2px; font-family: var(--font-mono); line-height: 1; }
  .up-stat-sub   { font-size: 11px; color: var(--text-muted); margin-top: 5px; }

  /* Table panel */
  .up-panel {
    background: var(--bg-card); border: 1px solid var(--border);
    border-radius: var(--radius-md); overflow: hidden;
  }
  .up-toolbar {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; padding: 14px 20px;
    border-bottom: 1px solid var(--border); flex-wrap: wrap;
  }
  .up-toolbar-left  { display: flex; align-items: center; gap: 10px; }
  .up-toolbar-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .up-panel-title { font-size: 13px; font-weight: 700; color: var(--text-primary); }
  .up-count-pill {
    padding: 2px 8px; border-radius: 99px; border: 1px solid var(--border-hi);
    font-size: 10px; color: var(--text-muted); font-family: var(--font-mono);
  }

  /* Filter tabs */
  .up-filter-tabs {
    display: flex; gap: 2px; padding: 4px;
    background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm);
  }
  .up-filter-tab {
    padding: 4px 12px; border-radius: 6px;
    font-size: 11px; font-weight: 500; text-transform: capitalize;
    cursor: pointer; transition: all 0.15s; border: none;
    background: none; color: var(--text-muted); font-family: var(--font-sans);
  }
  .up-filter-tab:hover { color: var(--text-primary); }
  .up-filter-tab.active {
    background: var(--accent-dim); color: var(--accent-light);
    box-shadow: inset 0 0 0 1px rgba(99,102,241,0.2);
  }
  .up-filter-tab-count { margin-left: 5px; opacity: 0.55; font-size: 10px; }

  /* Search */
  .up-search-wrap { position: relative; }
  .up-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; }
  .up-search {
    padding: 6px 12px 6px 30px;
    background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 12px;
    color: var(--text-primary); font-family: var(--font-sans);
    width: 210px; outline: none; transition: border-color 0.18s;
  }
  .up-search::placeholder { color: var(--text-muted); }
  .up-search:focus { border-color: rgba(99,102,241,0.4); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }

  /* Table */
  .up-table-wrap { overflow-x: auto; }
  .up-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .up-table thead tr { border-bottom: 1px solid var(--border); }
  .up-table th {
    padding: 10px 20px; text-align: left;
    font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px;
    text-transform: uppercase; color: var(--text-muted);
    white-space: nowrap; cursor: pointer; user-select: none; transition: color 0.15s;
  }
  .up-table th:hover { color: var(--text-secondary); }
  .up-table th.no-sort { cursor: default; }
  .up-table tbody tr {
    border-bottom: 1px solid rgba(255,255,255,0.035);
    transition: background 0.12s; cursor: pointer;
  }
  .up-table tbody tr:last-child { border-bottom: none; }
  .up-table tbody tr:hover { background: rgba(99,102,241,0.04); }
  .up-table td { padding: 14px 20px; vertical-align: middle; }

  /* Avatar cell */
  .up-avatar-cell { display: flex; align-items: center; gap: 12px; }
  .up-avatar {
    width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; border: 1.5px solid rgba(99,102,241,0.25);
  }
  .up-avatar-indigo { background: rgba(99,102,241,0.15);  color: #818cf8; }
  .up-avatar-green  { background: rgba(52,211,153,0.15);  color: #34d399; }
  .up-avatar-amber  { background: rgba(251,191,36,0.15);  color: #fbbf24; }
  .up-avatar-rose   { background: rgba(251,113,133,0.15); color: #fb7185; }
  .up-avatar-purple { background: rgba(167,139,250,0.15); color: #a78bfa; }
  .up-avatar-cyan   { background: rgba(34,211,238,0.15);  color: #22d3ee; }
  .up-cell-name  { font-size: 13px; font-weight: 500; color: var(--text-primary); }
  .up-cell-uid   { font-size: 10px; color: var(--text-muted); font-family: var(--font-mono); margin-top: 2px; }
  .up-cell-email { font-size: 12px; color: var(--text-secondary); }
  .up-cell-date  { font-size: 11px; color: var(--text-muted); font-family: var(--font-mono); white-space: nowrap; }

  /* Status chip */
  .up-chip {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 9px; border-radius: 99px;
    font-size: 10px; font-weight: 700; letter-spacing: 0.4px;
    text-transform: capitalize; border: 1px solid;
  }
  .up-chip.active    { background: var(--green-dim);  color: var(--green);  border-color: rgba(52,211,153,0.2);  }
  .up-chip.pending   { background: var(--amber-dim);  color: var(--amber);  border-color: rgba(251,191,36,0.2);  }
  .up-chip.suspended { background: var(--rose-dim);   color: var(--rose);   border-color: rgba(251,113,133,0.2); }
  .up-chip.inactive  { background: rgba(255,255,255,0.04); color: var(--text-muted); border-color: var(--border); }

  /* Row action buttons */
  .up-row-actions { display: flex; align-items: center; justify-content: flex-end; gap: 5px; }
  .up-icon-btn {
    width: 28px; height: 28px; border-radius: 7px;
    display: flex; align-items: center; justify-content: center;
    background: none; border: 1px solid var(--border);
    color: var(--text-muted); cursor: pointer; transition: all 0.15s; flex-shrink: 0;
  }
  .up-icon-btn:hover           { background: var(--bg-hover); color: var(--text-primary); border-color: var(--border-hi); }
  .up-icon-btn.activate:hover  { background: var(--green-dim); color: var(--green); border-color: rgba(52,211,153,0.3); }
  .up-icon-btn.suspend:hover   { background: var(--amber-dim); color: var(--amber); border-color: rgba(251,191,36,0.3); }

  /* Pagination */
  .up-pagination {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 20px; border-top: 1px solid var(--border);
  }
  .up-pag-info  { font-size: 11px; color: var(--text-muted); font-family: var(--font-mono); }
  .up-pag-pages { display: flex; gap: 4px; }
  .up-pag-btn {
    width: 28px; height: 28px; border-radius: 7px;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-family: var(--font-mono);
    border: 1px solid var(--border); background: none;
    color: var(--text-muted); cursor: pointer; transition: all 0.15s;
  }
  .up-pag-btn:hover  { background: var(--bg-hover); color: var(--text-primary); }
  .up-pag-btn.active { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* Skeleton */
  .up-skel {
    height: 10px; border-radius: 5px;
    background: linear-gradient(90deg, var(--bg-hover) 25%, rgba(99,102,241,0.05) 50%, var(--bg-hover) 75%);
    background-size: 200% 100%; animation: up-shimmer 1.8s infinite;
  }
  @keyframes up-shimmer { 0%{ background-position:200% 0 } 100%{ background-position:-200% 0 } }

  /* Empty */
  .up-empty {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 48px; gap: 8px;
    color: var(--text-muted); font-size: 12px;
  }

  /* Modal */
  .up-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.7);
    display: flex; align-items: center; justify-content: center;
    z-index: 50; padding: 16px; backdrop-filter: blur(4px);
  }
  .up-modal {
    background: var(--bg-elevated); border: 1px solid var(--border-hi);
    border-radius: var(--radius-lg); width: 100%; max-width: 660px;
    max-height: 90vh; display: flex; flex-direction: column;
    box-shadow: 0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(99,102,241,0.1);
  }
  .up-modal-sm  { max-width: 400px; }
  .up-modal-head {
    display: flex; align-items: flex-start; justify-content: space-between;
    padding: 20px 24px; border-bottom: 1px solid var(--border); flex-shrink: 0;
  }
  .up-modal-body { flex: 1; overflow-y: auto; padding: 24px; }
  .up-modal-body::-webkit-scrollbar { width: 4px; }
  .up-modal-body::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.25); border-radius: 99px; }
  .up-modal-foot {
    flex-shrink: 0; display: flex; align-items: center; justify-content: space-between;
    padding: 16px 24px; border-top: 1px solid var(--border);
  }
  .up-modal-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.3px; }
  .up-close-btn {
    width: 28px; height: 28px; border-radius: 7px;
    background: none; border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center;
    color: var(--text-muted); cursor: pointer; transition: all 0.15s; flex-shrink: 0;
  }
  .up-close-btn:hover { background: var(--rose-dim); color: var(--rose); border-color: rgba(251,113,133,0.25); }

  /* Modal user header */
  .up-user-head { display: flex; align-items: center; gap: 16px; }
  .up-user-avatar-lg {
    width: 52px; height: 52px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 17px; font-weight: 700;
    border: 2px solid rgba(99,102,241,0.3);
    box-shadow: 0 0 24px rgba(99,102,241,0.15);
  }
  .up-user-name   { font-size: 16px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.3px; }
  .up-user-meta   { font-size: 12px; color: var(--text-secondary); margin-top: 3px; display: flex; align-items: center; gap: 6px; }
  .up-user-chips  { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
  .up-user-joined { font-size: 10.5px; color: var(--text-muted); display: flex; align-items: center; gap: 4px; }

  /* Mini stat grid (modal) */
  .up-mini-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 10px; }
  .up-mini-stat {
    background: rgba(255,255,255,0.025); border: 1px solid var(--border);
    border-radius: var(--radius-sm); padding: 12px 14px;
    display: flex; align-items: center; gap: 12px;
  }
  .up-mini-icon { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .up-mini-label { font-size: 10px; color: var(--text-muted); margin-bottom: 3px; }
  .up-mini-value { font-size: 14px; font-weight: 600; color: var(--text-primary); font-family: var(--font-mono); }

  /* Txn table (modal) */
  .up-txn-table-wrap { background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); overflow: hidden; }
  .up-txn-table { width: 100%; border-collapse: collapse; }
  .up-txn-table th { padding: 8px 14px; text-align: left; font-size: 9px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); border-bottom: 1px solid var(--border); }
  .up-txn-table td { padding: 11px 14px; border-bottom: 1px solid rgba(255,255,255,0.03); }
  .up-txn-table tbody tr:last-child td { border-bottom: none; }
  .up-txn-table tbody tr:hover { background: rgba(99,102,241,0.03); }
  .up-txn-amount { font-size: 13px; font-weight: 600; color: var(--text-primary); font-family: var(--font-mono); }
  .up-txn-date   { font-size: 10.5px; color: var(--text-muted); font-family: var(--font-mono); white-space: nowrap; }

  /* Sub-label (modal) */
  .up-sub-label {
    font-size: 9.5px; font-weight: 700; letter-spacing: 1.2px;
    text-transform: uppercase; color: var(--text-muted); margin-bottom: 10px;
    display: flex; align-items: center; gap: 6px;
  }
  .up-sub-label::after { content:''; flex:1; height:1px; background: var(--border); }

  /* Modal action buttons */
  .up-act-danger {
    display: flex; align-items: center; gap: 6px;
    padding: 7px 14px; border-radius: var(--radius-sm);
    background: var(--rose-dim); border: 1px solid rgba(251,113,133,0.25);
    color: var(--rose); font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
  }
  .up-act-danger:hover { background: rgba(251,113,133,0.25); box-shadow: 0 0 16px rgba(251,113,133,0.15); }
  .up-act-green {
    display: flex; align-items: center; gap: 6px;
    padding: 7px 14px; border-radius: var(--radius-sm);
    background: var(--green-dim); border: 1px solid rgba(52,211,153,0.25);
    color: var(--green); font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
  }
  .up-act-green:hover { background: rgba(52,211,153,0.25); }
  .up-act-amber {
    display: flex; align-items: center; gap: 6px;
    padding: 7px 14px; border-radius: var(--radius-sm);
    background: var(--amber-dim); border: 1px solid rgba(251,191,36,0.25);
    color: var(--amber); font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
  }
  .up-act-amber:hover { background: rgba(251,191,36,0.25); }
  .up-act-ghost {
    display: flex; align-items: center; gap: 6px;
    padding: 7px 14px; border-radius: var(--radius-sm);
    background: none; border: 1px solid var(--border-hi);
    color: var(--text-secondary); font-size: 12px; font-weight: 500;
    cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
  }
  .up-act-ghost:hover { background: var(--bg-hover); color: var(--text-primary); }

  /* Confirm modal */
  .up-confirm-msg { font-size: 13px; color: var(--text-secondary); line-height: 1.55; }

  /* Add user form */
  .up-form-label { font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; letter-spacing: 0.2px; }
  .up-form-input {
    width: 100%; padding: 9px 12px;
    background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 13px;
    color: var(--text-primary); font-family: var(--font-sans);
    outline: none; transition: border-color 0.18s;
  }
  .up-form-input::placeholder { color: var(--text-muted); }
  .up-form-input:focus { border-color: rgba(99,102,241,0.45); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }
  .up-form-row2  { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .up-form-group { display: flex; flex-direction: column; }

  /* Status pick pills */
  .up-status-pick { display: flex; gap: 6px; }
  .up-status-pill {
    padding: 5px 12px; border-radius: 99px;
    font-size: 11px; font-weight: 600; text-transform: capitalize;
    cursor: pointer; transition: all 0.15s; border: 1px solid var(--border);
    background: none; color: var(--text-muted); font-family: var(--font-sans);
  }
  .up-status-pill.active-sel    { background: var(--green-dim);  color: var(--green);  border-color: rgba(52,211,153,0.3);  }
  .up-status-pill.pending-sel   { background: var(--amber-dim);  color: var(--amber);  border-color: rgba(251,191,36,0.3);  }
  .up-status-pill.suspended-sel { background: var(--rose-dim);   color: var(--rose);   border-color: rgba(251,113,133,0.3); }

  /* Error banner */
  .up-error-banner {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 14px; border-radius: var(--radius-sm);
    background: rgba(251,113,133,0.08); border: 1px solid rgba(251,113,133,0.22);
    color: var(--rose); font-size: 12px;
  }

  /* Toast */
  .up-toast {
    position: fixed; bottom: 24px; right: 24px; z-index: 100;
    display: flex; align-items: center; gap: 10px;
    padding: 12px 18px; border-radius: var(--radius-md);
    background: var(--bg-elevated); border: 1px solid var(--border-hi);
    box-shadow: 0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,102,241,0.1);
    font-size: 13px; color: var(--text-primary);
    animation: up-slide-in 0.22s ease;
  }
  .up-toast.ok  { border-color: rgba(52,211,153,0.3);  }
  .up-toast.err { border-color: rgba(251,113,133,0.3); }
  @keyframes up-slide-in { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }

  /* Session gate */
  .up-gate {
    height: 100vh; display: flex; align-items: center; justify-content: center;
    flex-direction: column; gap: 14px; background: var(--bg-base);
  }
  .up-gate-icon {
    width: 36px; height: 36px; border-radius: 10px;
    background: linear-gradient(135deg,#6366f1,#a78bfa);
    box-shadow: 0 0 24px rgba(99,102,241,0.4);
    display: flex; align-items: center; justify-content: center;
  }
  .up-gate-text { font-size: 12px; color: var(--text-muted); font-family: var(--font-sans); }

  /* ═══════════════════════════════════════
     MOBILE SIDEBAR OVERLAY + HAMBURGER
  ════════════════════════════════════════ */
  .ad-sidebar-overlay {
    display: none;
    position: fixed; inset: 0; background: rgba(0,0,0,0.6);
    z-index: 40; backdrop-filter: blur(2px);
    animation: overlay-fade-in 0.22s var(--transition-smooth);
  }
  @keyframes overlay-fade-in { from { opacity:0 } to { opacity:1 } }

  .up-hamburger {
    display: none;
    width: 36px; height: 36px; border-radius: var(--radius-sm);
    background: var(--bg-card); border: 1px solid var(--border-hi);
    color: var(--text-secondary); cursor: pointer;
    align-items: center; justify-content: center;
    transition: all 0.15s; flex-shrink: 0;
  }
  .up-hamburger:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* ═══════════════════════════════════════
     RESPONSIVE BREAKPOINTS
  ════════════════════════════════════════ */

  /* ── Large desktop (≥1400px): wider comfortable layout ── */
  @media (min-width: 1400px) {
    .up-stat-grid { grid-template-columns: repeat(4, 1fr); gap: 18px; }
    .up-main { padding: 32px 36px; }
    .up-topbar { padding: 0 36px; }
    .up-stat-value { font-size: 32px; }
  }

  /* ── Medium desktop (1024px – 1279px): tighten up ── */
  @media (max-width: 1279px) {
    :root { --sidebar-w: 210px; }
    .up-main { padding: 22px 22px; }
    .up-topbar { padding: 0 22px; }
    .up-stat-grid { gap: 12px; }
    .up-mini-grid { grid-template-columns: repeat(3,1fr); }
  }

  /* ── Tablet landscape (768px – 1023px): collapse sidebar to icon strip ── */
  @media (max-width: 1023px) {
    :root { --sidebar-w: 60px; }

    .ad-logo-text,
    .ad-nav-section-label,
    .ad-nav-link span,
    .ad-nav-chevron,
    .ad-user-info,
    .ad-logout-btn { display: none !important; }

    .ad-logo { justify-content: center; padding: 0; }
    .ad-logo-icon { width: 36px; height: 36px; }

    .ad-nav { padding: 14px 6px; gap: 14px; }
    .ad-nav-items { gap: 2px; }
    .ad-nav-link {
      justify-content: center; padding: 10px;
      gap: 0;
    }
    .ad-nav-link svg { opacity: 0.7; }
    .ad-nav-link.active svg { opacity: 1; }
    .ad-nav-link.active::before { top: 10%; bottom: 10%; }

    .ad-user-card { padding: 10px 6px; }
    .ad-user-inner { justify-content: center; padding: 8px; gap: 0; }

    .up-stat-grid { grid-template-columns: repeat(2,1fr); }
    .up-mini-grid { grid-template-columns: repeat(3,1fr); }
    .up-main { padding: 18px 18px; gap: 16px; }
    .up-topbar { padding: 0 18px; }
    .up-toolbar { flex-direction: column; align-items: stretch; gap: 10px; }
    .up-toolbar-right { flex-wrap: wrap; }
    .up-search { width: 100%; }
    .up-filter-tabs { flex-wrap: wrap; }
  }

  /* ── Mobile (≤767px): hide sidebar entirely, show hamburger drawer ── */
  @media (max-width: 767px) {
    :root { --sidebar-w: 232px; }

    .up-hamburger { display: flex; }

    .ad-sidebar {
      position: fixed; top: 0; left: 0; height: 100vh;
      z-index: 50; transform: translateX(-100%);
      transition: transform 0.28s var(--transition-smooth);
      box-shadow: 4px 0 40px rgba(0,0,0,0.5);
      overflow: hidden;
    }
    .ad-sidebar.open {
      transform: translateX(0);
    }
    .ad-sidebar-overlay.open { display: block; }

    /* Restore hidden items inside open mobile drawer */
    .ad-sidebar.open .ad-logo-text,
    .ad-sidebar.open .ad-nav-section-label,
    .ad-sidebar.open .ad-nav-link span,
    .ad-sidebar.open .ad-nav-chevron,
    .ad-sidebar.open .ad-user-info,
    .ad-sidebar.open .ad-logout-btn { display: initial !important; }

    .ad-sidebar.open .ad-sidebar-close { display: flex !important; }
    .ad-sidebar.open .ad-logo { justify-content: flex-start; padding: 0 18px; }
    .ad-sidebar.open .ad-nav { padding: 18px 10px; gap: 22px; }
    .ad-sidebar.open .ad-nav-link { justify-content: flex-start; padding: 8px 10px; gap: 9px; }
    .ad-sidebar.open .ad-nav-link span { display: block !important; flex: 1; }
    .ad-sidebar.open .ad-user-inner { justify-content: flex-start; gap: 10px; padding: 9px 10px; }
    .ad-sidebar.open .ad-nav-section-label { display: block !important; }

    .up-shell { overflow: hidden; }
    .up-page { width: 100%; }

    .up-stat-grid { grid-template-columns: repeat(2,1fr); gap: 10px; }
    .up-stat-value { font-size: 22px; }
    .up-stat { padding: 14px; }

    .up-main { padding: 14px 12px; gap: 14px; }
    .up-topbar { padding: 0 14px; }
    .up-topbar-sub { display: none; }
    .up-topbar-title { font-size: 13px; }

    .up-toolbar { padding: 10px 14px; flex-direction: column; align-items: stretch; gap: 10px; }
    .up-toolbar-right { flex-direction: column; align-items: stretch; gap: 8px; }
    .up-search { width: 100%; }
    .up-filter-tabs { flex-wrap: wrap; }

    .up-table th, .up-table td { padding: 10px 12px; }
    .up-cell-email { display: none; }
    .up-table th:nth-child(2), .up-table td:nth-child(2) { display: none; }

    .up-mini-grid { grid-template-columns: repeat(2,1fr); }
    .up-modal { max-height: 95vh; border-radius: var(--radius-md) var(--radius-md) 0 0; }
    .up-overlay { align-items: flex-end; padding: 0; }

    .up-pagination { flex-direction: column; align-items: flex-start; gap: 10px; padding: 10px 14px; }

    .up-toast { bottom: 16px; right: 12px; left: 12px; font-size: 12px; }

    .up-btn span, .up-btn-label { display: none; }
    .up-topbar-right { gap: 6px; }

    .up-modal-foot { flex-wrap: wrap; gap: 8px; }
    .up-modal-head { padding: 16px 16px; }
    .up-modal-body { padding: 16px; }
    .up-modal-foot { padding: 12px 16px; }

    .up-form-row2 { grid-template-columns: 1fr; }

    .up-pag-pages { flex-wrap: wrap; }
  }

  /* ── Small mobile (≤480px): extra compact ── */
  @media (max-width: 480px) {
    .up-stat-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
    .up-stat-value { font-size: 20px; }
    .up-stat-sub { font-size: 9px; }
    .up-stat { padding: 12px; gap: 10px; }
    .up-stat-icon { width: 28px; height: 28px; }

    .up-mini-grid { grid-template-columns: 1fr 1fr; }

    .up-topbar-badge { display: none; }

    /* Hide plan and joined columns on very small screens */
    .up-table th:nth-child(4), .up-table td:nth-child(4),
    .up-table th:nth-child(5), .up-table td:nth-child(5) { display: none; }

    .up-cell-uid { display: none; }
    .up-avatar { width: 28px; height: 28px; font-size: 9px; }
    .up-avatar-cell { gap: 8px; }
  }
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─── Sidebar link ─────────────────────────────────────────────────────────────
function SidebarLink({ to, label, icon: Icon, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => `ad-nav-link${isActive ? " active" : ""}`}
    >
      {({ isActive }) => (
        <>
          <Icon size={14} />
          <span style={{ flex: 1 }}>{label}</span>
          {isActive && <ChevronRight size={10} className="ad-nav-chevron" />}
        </>
      )}
    </NavLink>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtDateTime(str) {
  if (!str) return "—";
  return new Date(str).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function fmtCurrency(val) {
  if (val == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(val);
}
function initials(first, last) {
  return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?";
}
const AVATAR_PALETTE = ["indigo", "green", "amber", "rose", "purple", "cyan"];
function avatarColor(id) {
  if (!id) return AVATAR_PALETTE[0];
  const n = typeof id === "string" ? id.charCodeAt(0) + id.charCodeAt(id.length - 1) : id;
  return AVATAR_PALETTE[n % AVATAR_PALETTE.length];
}

// ─── Status chip ──────────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const IconMap = { active: CheckCircle2, pending: Clock, suspended: XCircle, inactive: XCircle };
  const Icon = IconMap[status] ?? Clock;
  const cls  = ["active", "pending", "suspended", "inactive"].includes(status) ? status : "inactive";
  return (
    <span className={`up-chip ${cls}`}>
      <Icon size={9} />{status ?? "unknown"}
    </span>
  );
}

// ─── Empty ────────────────────────────────────────────────────────────────────
function Empty({ message = "No data" }) {
  return (
    <div className="up-empty">
      <AlertCircle size={18} />
      <span>{message}</span>
    </div>
  );
}

// ─── Confirm modal ────────────────────────────────────────────────────────────
function ConfirmModal({ title, message, confirmLabel, confirmVariant, onConfirm, onClose, busy }) {
  return (
    <div className="up-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="up-modal up-modal-sm">
        <div className="up-modal-head">
          <span className="up-modal-title">{title}</span>
          <button className="up-close-btn" onClick={onClose}><X size={13} /></button>
        </div>
        <div className="up-modal-body">
          <p className="up-confirm-msg">{message}</p>
        </div>
        <div className="up-modal-foot" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button className="up-act-ghost" onClick={onClose}>Cancel</button>
          <button
            className={confirmVariant === "danger" ? "up-act-danger" : "up-act-green"}
            onClick={onConfirm} disabled={busy}
            style={{ opacity: busy ? 0.45 : 1 }}
          >
            {busy ? "Please wait…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── User detail modal ────────────────────────────────────────────────────────
function UserModal({ user, onClose, onStatusChange, onDelete, showToast }) {
  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [acting,  setActing]  = useState(false);

  useEffect(() => { loadDetail(); }, [user.id]);

  async function loadDetail() {
    setLoading(true);
    try {
      const uid = user.id;
      const [
        // wallets → user_id FK confirmed in schema
        { data: walletRows },
        // transactions → user_id FK confirmed; join wallet for reference
        { data: txns },
        // open_positions → user_id FK confirmed; filter status = 'open'
        { count: openTrades },
        // stock_orders → user_id FK confirmed
        { count: stockOrders },
        // support_tickets → user_id FK confirmed; filter status = 'open'
        { count: openTickets },
        // notifications → user_id FK confirmed; total count
        { count: unreadNotifs },
        // user_subscriptions → user_id + plan_id FKs confirmed; join subscription_plans
        { data: subRows },
        // copy_relationships → copier_id FK to users
        { count: copyCount },
        // hire_agreements → client_id FK to users; active ones
        { count: hireCount },
        // broker_connections → user_id FK
        { count: brokerCount },
        // payment_methods → user_id FK
        { count: paymentMethodCount },
        // portfolio_holdings → user_id FK
        { count: holdingCount },
        // price_alerts → user_id FK
        { count: alertCount },
      ] = await Promise.all([
        supabase.from("wallets")
          .select("id, balance")
          .eq("user_id", uid),

        supabase.from("transactions")
          .select("id, type, amount, status, created_at, wallets(id)")
          .eq("user_id", uid)
          .order("created_at", { ascending: false })
          .limit(8),

        supabase.from("open_positions")
          .select("*", { count: "exact", head: true })
          .eq("user_id", uid)
          .eq("status", "open"),

        supabase.from("stock_orders")
          .select("*", { count: "exact", head: true })
          .eq("user_id", uid),

        supabase.from("support_tickets")
          .select("*", { count: "exact", head: true })
          .eq("user_id", uid)
          .eq("status", "open"),

        supabase.from("notifications")
          .select("*", { count: "exact", head: true })
          .eq("user_id", uid),

        supabase.from("user_subscriptions")
          .select("id, status, created_at, subscription_plans(id, name)")
          .eq("user_id", uid)
          .order("created_at", { ascending: false }),

        supabase.from("copy_relationships")
          .select("*", { count: "exact", head: true })
          .eq("copier_id", uid),

        supabase.from("hire_agreements")
          .select("*", { count: "exact", head: true })
          .eq("client_id", uid)
          .eq("status", "active"),

        supabase.from("broker_connections")
          .select("*", { count: "exact", head: true })
          .eq("user_id", uid),

        supabase.from("payment_methods")
          .select("*", { count: "exact", head: true })
          .eq("user_id", uid),

        supabase.from("portfolio_holdings")
          .select("*", { count: "exact", head: true })
          .eq("user_id", uid),

        supabase.from("price_alerts")
          .select("*", { count: "exact", head: true })
          .eq("user_id", uid),
      ]);

      // Wallet: take highest balance if multiple wallets exist
      const wallet = walletRows?.length
        ? walletRows.reduce((a, b) => (b.balance > a.balance ? b : a), walletRows[0])
        : null;

      // Active subscription — most recent active plan wins
      const activeSub = (subRows ?? []).find((s) => s.status === "active") ?? subRows?.[0] ?? null;

      setDetail({
        wallet,
        txns:             txns ?? [],
        openTrades:       openTrades  ?? 0,
        stockOrders:      stockOrders ?? 0,
        openTickets:      openTickets ?? 0,
        unreadNotifs:     unreadNotifs ?? 0,
        subscription:     activeSub,
        copyCount:        copyCount   ?? 0,
        hireCount:        hireCount   ?? 0,
        brokerCount:      brokerCount ?? 0,
        paymentMethodCount: paymentMethodCount ?? 0,
        holdingCount:     holdingCount ?? 0,
        alertCount:       alertCount  ?? 0,
      });
    } catch (e) {
      console.error("loadDetail:", e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleAction() {
    if (!confirm) return;
    setActing(true);
    try {
      if (confirm.type === "delete") {
        const { error } = await supabase.from("users").delete().eq("id", user.id);
        if (error) throw error;
        showToast("User deleted.");
        onDelete(user.id);
        onClose();
      } else {
        const newStatus = confirm.type === "suspend" ? "suspended" : "active";
        const { error } = await supabase.from("users").update({ status: newStatus }).eq("id", user.id);
        if (error) throw error;
        showToast(`User ${newStatus === "active" ? "activated" : "suspended"}.`);
        onStatusChange(user.id, newStatus);
      }
      setConfirm(null);
    } catch (e) {
      showToast(e.message ?? "Action failed.", false);
    } finally {
      setActing(false);
    }
  }

  const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || "Unknown";
  const col = avatarColor(user.id);

  const CONFIRM_CFG = {
    suspend:  { title: "Suspend account",  message: `Suspend ${fullName}'s account? They will lose platform access immediately.`, confirmLabel: "Suspend",            confirmVariant: "danger" },
    activate: { title: "Activate account", message: `Reactivate ${fullName}'s account? They will regain full platform access.`,   confirmLabel: "Activate",           confirmVariant: "green"  },
    delete:   { title: "Delete user",      message: `Permanently delete ${fullName}? This cannot be undone and removes all associated data.`, confirmLabel: "Delete permanently", confirmVariant: "danger" },
  };

  function txnTypeStyle(type) {
    if (type === "deposit")    return { background: "var(--green-dim)",  color: "var(--green)"        };
    if (type === "withdrawal") return { background: "var(--rose-dim)",   color: "var(--rose)"         };
    return                            { background: "var(--accent-dim)", color: "var(--accent-light)" };
  }
  function txnStatusColor(s) {
    if (s === "approved")  return "var(--green)";
    if (s === "pending")   return "var(--amber)";
    if (s === "cancelled") return "var(--rose)";
    return "var(--text-muted)";
  }

  return (
    <>
      <div className="up-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="up-modal">
          {/* Head */}
          <div className="up-modal-head">
            <div className="up-user-head">
              <div className={`up-user-avatar-lg up-avatar-${col}`}>
                {initials(user.first_name, user.last_name)}
              </div>
              <div>
                <div className="up-user-name">{fullName}</div>
                <div className="up-user-meta"><Mail size={10} />{user.email}</div>
                <div className="up-user-chips">
                  <StatusChip status={user.status} />
                  <span className="up-user-joined"><Calendar size={9} />Joined {fmtDate(user.created_at)}</span>
                </div>
              </div>
            </div>
            <button className="up-close-btn" onClick={onClose}><X size={13} /></button>
          </div>

          {/* Body */}
          <div className="up-modal-body" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {loading ? (
              <div className="up-mini-grid">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} style={{ height: 60, borderRadius: 8 }} className="up-skel" />
                ))}
              </div>
            ) : (
              <>
                <div>
                  <div className="up-sub-label">Account overview</div>
                  <div className="up-mini-grid">
                    {[
                      { label: "Wallet balance",   value: fmtCurrency(detail.wallet?.balance),                            icon: Wallet,          bg: "var(--green-dim)",   color: "var(--green)"         },
                      { label: "Subscription",     value: detail.subscription?.subscription_plans?.name ?? "None",        icon: Activity,        bg: "var(--cyan-dim)",    color: "var(--cyan)"          },
                      { label: "Open trades",      value: detail.openTrades       ?? 0,                                   icon: CandlestickChart, bg: "var(--amber-dim)",  color: "var(--amber)"         },
                      { label: "Stock orders",     value: detail.stockOrders      ?? 0,                                   icon: BarChart2,       bg: "var(--accent-dim)",  color: "var(--accent-light)"  },
                      { label: "Copy trades",      value: detail.copyCount        ?? 0,                                   icon: Copy,            bg: "var(--purple-dim)",  color: "var(--purple)"        },
                      { label: "Hire agreements",  value: detail.hireCount        ?? 0,                                   icon: UserCheck,       bg: "var(--orange-dim)",  color: "var(--orange)"        },
                      { label: "Open tickets",     value: detail.openTickets      ?? 0,                                   icon: HeadphonesIcon,  bg: "var(--rose-dim)",    color: "var(--rose)"          },
                      { label: "Notifications",      value: detail.unreadNotifs     ?? 0,                                   icon: Bell,            bg: "var(--amber-dim)",   color: "var(--amber)"         },
                      { label: "Broker conns",     value: detail.brokerCount      ?? 0,                                   icon: Activity,        bg: "var(--blue-dim)",    color: "var(--blue)"          },
                      { label: "Payment methods",  value: detail.paymentMethodCount ?? 0,                                 icon: Receipt,         bg: "var(--green-dim)",   color: "var(--green)"         },
                      { label: "Holdings",         value: detail.holdingCount     ?? 0,                                   icon: BarChart2,       bg: "var(--cyan-dim)",    color: "var(--cyan)"          },
                      { label: "Price alerts",     value: detail.alertCount       ?? 0,                                   icon: Bell,            bg: "var(--rose-dim)",    color: "var(--rose)"          },
                    ].map(({ label, value, icon: Icon, bg, color }) => (
                      <div className="up-mini-stat" key={label}>
                        <div className="up-mini-icon" style={{ background: bg }}>
                          <Icon size={14} style={{ color }} />
                        </div>
                        <div>
                          <div className="up-mini-label">{label}</div>
                          <div className="up-mini-value">{value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="up-sub-label">Recent transactions</div>
                  {detail.txns.length === 0 ? (
                    <Empty message="No transactions yet." />
                  ) : (
                    <div className="up-txn-table-wrap">
                      <table className="up-txn-table">
                        <thead><tr>{["Type","Amount","Status","Date"].map((h) => <th key={h}>{h}</th>)}</tr></thead>
                        <tbody>
                          {detail.txns.map((t) => (
                            <tr key={t.id}>
                              <td>
                                <span style={{ ...txnTypeStyle(t.type), padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 700, textTransform: "capitalize" }}>
                                  {t.type}
                                </span>
                              </td>
                              <td><span className="up-txn-amount">{fmtCurrency(t.amount)}</span></td>
                              <td><span style={{ fontSize: 11, fontWeight: 600, textTransform: "capitalize", color: txnStatusColor(t.status) }}>{t.status}</span></td>
                              <td><span className="up-txn-date">{fmtDateTime(t.created_at)}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="up-modal-foot">
            <button className="up-act-danger" onClick={() => setConfirm({ type: "delete" })}>
              <Trash2 size={12} />Delete user
            </button>
            <div style={{ display: "flex", gap: 8 }}>
              {user.status !== "active" ? (
                <button className="up-act-green" onClick={() => setConfirm({ type: "activate" })}>
                  <ShieldCheck size={12} />Activate
                </button>
              ) : (
                <button className="up-act-amber" onClick={() => setConfirm({ type: "suspend" })}>
                  <ShieldOff size={12} />Suspend
                </button>
              )}
              <button className="up-act-ghost" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>

      {confirm && (
        <ConfirmModal
          {...CONFIRM_CFG[confirm.type]}
          onConfirm={handleAction}
          onClose={() => setConfirm(null)}
          busy={acting}
        />
      )}
    </>
  );
}

// ─── Add user modal ───────────────────────────────────────────────────────────
function AddUserModal({ onClose, onSuccess }) {
  const [form,    setForm]    = useState({ first_name: "", last_name: "", email: "", password: "", status: "active" });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  function set(key, val) { setForm((f) => ({ ...f, [key]: val })); }

  async function handleSubmit() {
    setError("");
    if (!form.first_name.trim() || !form.email.trim() || !form.password.trim()) {
      setError("First name, email and password are required."); return;
    }
    setLoading(true);
    try {
      const { data: authData, error: authErr } = await supabase.auth.signUp({ email: form.email, password: form.password });
      if (authErr) throw authErr;
      const { error: profileErr } = await supabase.from("users").insert({
        id: authData?.user?.id,
        first_name: form.first_name.trim(),
        last_name:  form.last_name.trim() || null,
        email:      form.email.trim(),
        status:     "active",
        created_at: new Date().toISOString(),
      });
      if (profileErr) throw profileErr;
      onSuccess("User created successfully.");
      onClose();
    } catch (e) {
      setError(e.message ?? "Failed to create user.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="up-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="up-modal up-modal-sm">
        <div className="up-modal-head">
          <span className="up-modal-title">Add new user</span>
          <button className="up-close-btn" onClick={onClose}><X size={13} /></button>
        </div>
        <div className="up-modal-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="up-form-row2">
            <div className="up-form-group">
              <div className="up-form-label">First name *</div>
              <input className="up-form-input" placeholder="John" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
            </div>
            <div className="up-form-group">
              <div className="up-form-label">Last name</div>
              <input className="up-form-input" placeholder="Doe" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
            </div>
          </div>
          <div className="up-form-group">
            <div className="up-form-label">Email address *</div>
            <input className="up-form-input" type="email" placeholder="john@example.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div className="up-form-group">
            <div className="up-form-label">Password *</div>
            <input className="up-form-input" type="password" placeholder="••••••••" value={form.password} onChange={(e) => set("password", e.target.value)} />
          </div>
          <div className="up-form-group">
            <div className="up-form-label">Initial status</div>
            <div className="up-status-pick">
              {["active", "pending", "suspended"].map((s) => (
                <button key={s} className={`up-status-pill ${form.status === s ? `${s}-sel` : ""}`} onClick={() => set("status", s)}>{s}</button>
              ))}
            </div>
          </div>
          {error && (
            <div className="up-error-banner">
              <AlertCircle size={13} style={{ flexShrink: 0 }} />{error}
            </div>
          )}
        </div>
        <div className="up-modal-foot" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button className="up-act-ghost" onClick={onClose}>Cancel</button>
          <button
            onClick={handleSubmit} disabled={loading}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "7px 18px", borderRadius: "var(--radius-sm)",
              background: "var(--accent)", border: "none", color: "#fff",
              fontSize: 12, fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.5 : 1, fontFamily: "var(--font-sans)",
            }}
          >
            {loading ? "Creating…" : "Create user"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function UsersPage() {
  const navigate = useNavigate();

  // ── Session state ──────────────────────────────────────────────────────────
  const [session,        setSession]        = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);

  // ── Page data ──────────────────────────────────────────────────────────────
  const [users,        setUsers]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField,    setSortField]    = useState("created_at");
  const [sortDir,      setSortDir]      = useState(-1);
  const [page,         setPage]         = useState(1);
  const [viewUser,     setViewUser]     = useState(null);
  const [showAdd,      setShowAdd]      = useState(false);
  const [toast,        setToast]        = useState({ msg: "", ok: true });
  const [sidebarOpen,  setSidebarOpen]  = useState(false);

  // ── Session guard ──────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!s) {
        setSessionLoading(false);
        navigate("/admin/login", { replace: true });
      } else {
        if (!alive) return;
        setSession(s);
        setSessionLoading(false);
        fetchUsers();
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!s) navigate("/admin/login", { replace: true });
      else setSession(s);
    });

    return () => { alive = false; subscription.unsubscribe(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await supabase.auth.signOut();
    // onAuthStateChange will handle the redirect
  }

  // ── Data ───────────────────────────────────────────────────────────────────
  async function fetchUsers() {
    setLoading(true);
    try {
      // 1. Core user rows — select only columns that exist on the users table.
      //    Wallet balance and subscription are fetched separately to avoid
      //    Supabase confusing nested `status` columns with users.status.
      const { data: rows, error } = await supabase
        .from("users")
        .select("id, first_name, last_name, email, status, created_at")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // 2. Parallel supporting queries — all keyed by user_id
      const [
        { data: walletRows },
        { data: subRows },
        { data: txnRows },
        { data: tradeRows },
        { data: stockRows },
        { data: ticketRows },
        { data: notifRows },
        { data: copyRows },
        { data: hireRows },
      ] = await Promise.all([
        supabase.from("wallets").select("user_id, balance"),
        supabase.from("user_subscriptions").select("user_id, status, subscription_plans(name)"),
        supabase.from("transactions").select("user_id"),
        supabase.from("open_positions").select("user_id, status"),
        supabase.from("stock_orders").select("user_id"),
        supabase.from("support_tickets").select("user_id, status"),
        supabase.from("notifications").select("user_id"),
        supabase.from("copy_relationships").select("copier_id"),
        supabase.from("hire_agreements").select("client_id, status"),
      ]);

      // 3. Build O(1) lookup maps keyed by user_id
      const countMap = (arr, keyFn, filterFn = () => true) => {
        const m = {};
        (arr ?? []).forEach((r) => {
          if (filterFn(r)) { const k = keyFn(r); m[k] = (m[k] ?? 0) + 1; }
        });
        return m;
      };

      // Wallet: one row per user_id, take the highest balance if multiple
      const walletMap = {};
      (walletRows ?? []).forEach((w) => {
        if (walletMap[w.user_id] == null || w.balance > walletMap[w.user_id]) {
          walletMap[w.user_id] = w.balance ?? 0;
        }
      });

      // Active subscription name per user
      const subMap = {};
      (subRows ?? []).forEach((s) => {
        if (s.status === "active" && !subMap[s.user_id]) {
          subMap[s.user_id] = s.subscription_plans?.name ?? null;
        }
      });

      const txnCount       = countMap(txnRows,   (r) => r.user_id);
      const openTradeCount = countMap(tradeRows,  (r) => r.user_id,   (r) => r.status === "open");
      const stockCount     = countMap(stockRows,  (r) => r.user_id);
      const openTicketCnt  = countMap(ticketRows, (r) => r.user_id,   (r) => r.status === "open");
      const unreadNotif    = countMap(notifRows,  (r) => r.user_id);
      const copyCount      = countMap(copyRows,   (r) => r.copier_id);
      const hireCount      = countMap(hireRows,   (r) => r.client_id, (r) => r.status === "active");

      // 4. Merge everything onto each user row
      const enriched = (rows ?? []).map((u) => ({
        ...u,
        wallet_balance:      walletMap[u.id]    ?? 0,
        subscription_name:   subMap[u.id]       ?? null,
        txn_count:           txnCount[u.id]     ?? 0,
        open_trade_count:    openTradeCount[u.id] ?? 0,
        stock_count:         stockCount[u.id]   ?? 0,
        open_ticket_count:   openTicketCnt[u.id] ?? 0,
        unread_notif_count:  unreadNotif[u.id]  ?? 0,
        copy_count:          copyCount[u.id]    ?? 0,
        hire_count:          hireCount[u.id]    ?? 0,
      }));

      setUsers(enriched);
    } catch (e) {
      console.error("fetchUsers:", e.message);
    } finally {
      setLoading(false);
    }
  }

  function showToast(msg, ok = true) {
    setToast({ msg, ok });
    setTimeout(() => setToast({ msg: "", ok: true }), 3000);
  }

  const handleStatusChange = useCallback((userId, newStatus) => {
    setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, status: newStatus } : u));
    setViewUser((prev) => prev?.id === userId ? { ...prev, status: newStatus } : prev);
  }, []);

  const handleDelete = useCallback((userId) => {
    setUsers((prev) => prev.filter((u) => u.id !== userId));
    setViewUser(null);
  }, []);

  // ── Filter + sort ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = users.filter((u) => {
      const matchSearch = !q
        || `${u.first_name ?? ""} ${u.last_name ?? ""}`.toLowerCase().includes(q)
        || u.email?.toLowerCase().includes(q);
      const matchStatus = statusFilter === "all" || u.status === statusFilter;
      return matchSearch && matchStatus;
    });
    list.sort((a, b) => {
      if (sortField === "name")    return `${a.first_name}${a.last_name}`.localeCompare(`${b.first_name}${b.last_name}`) * sortDir;
      if (sortField === "email")   return (a.email ?? "").localeCompare(b.email ?? "") * sortDir;
      if (sortField === "balance") return ((a.wallet_balance ?? 0) - (b.wallet_balance ?? 0)) * sortDir;
      if (sortField === "plan")    return (a.subscription_name ?? "").localeCompare(b.subscription_name ?? "") * sortDir;
      return (new Date(a.created_at ?? 0) - new Date(b.created_at ?? 0)) * sortDir;
    });
    return list;
  }, [users, search, statusFilter, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageSlice  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  function handleSort(field) {
    if (field === sortField) setSortDir((d) => d * -1);
    else { setSortField(field); setSortDir(-1); }
    setPage(1);
  }

  const stats = useMemo(() => ({
    total:     users.length,
    active:    users.filter((u) => u.status === "active").length,
    pending:   users.filter((u) => u.status === "pending").length,
    suspended: users.filter((u) => u.status === "suspended").length,
  }), [users]);

  const STATUS_FILTERS = ["all", "active", "pending", "suspended"];

  function SortIcon({ field }) {
    if (field !== sortField) return <ChevronUp size={9} style={{ opacity: 0.2, marginLeft: 4, display: "inline" }} />;
    return sortDir === 1
      ? <ChevronUp   size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />
      : <ChevronDown size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />;
  }

  const adminEmail    = session?.user?.email ?? "admin@Tradexflow.io";
  const adminInitials = adminEmail.slice(0, 2).toUpperCase();

  // ── Session gate ───────────────────────────────────────────────────────────
  if (sessionLoading) {
    return (
      <>
        <StyleInjector />
        <div className="up-gate">
          <div className="up-gate-icon"><Shield size={16} color="#fff" /></div>
          <div className="up-gate-text">Verifying session…</div>
        </div>
      </>
    );
  }

  const STAT_CARDS = [
    { label: "Total users", value: stats.total,     icon: Users,        bg: "var(--accent-dim)",  color: "var(--accent-light)", glow: "rgba(99,102,241,0.08)",  bar: "var(--accent-light)", sub: "All registered"        },
    { label: "Active",      value: stats.active,    icon: CheckCircle2, bg: "var(--green-dim)",   color: "var(--green)",        glow: "rgba(52,211,153,0.08)",  bar: "var(--green)",        sub: "Full access"           },
    { label: "Pending",     value: stats.pending,   icon: Clock,        bg: "var(--amber-dim)",   color: "var(--amber)",        glow: "rgba(251,191,36,0.08)",  bar: "var(--amber)",        sub: "Awaiting verification" },
    { label: "Suspended",   value: stats.suspended, icon: XCircle,      bg: "var(--rose-dim)",    color: "var(--rose)",         glow: "rgba(251,113,133,0.08)", bar: "var(--rose)",         sub: "Access revoked"        },
  ];

  return (
    <>
      <StyleInjector />
      <div className="up-shell">

        {/* ── Mobile sidebar overlay ── */}
        <div
          className={`ad-sidebar-overlay${sidebarOpen ? " open" : ""}`}
          onClick={() => setSidebarOpen(false)}
        />

        {/* ── Sidebar ── */}
        <aside className={`ad-sidebar${sidebarOpen ? " open" : ""}`}>
          {/* Logo */}
          <div className="ad-logo">
            <div className="ad-logo-icon">
              <Shield size={13} color="#fff" />
            </div>
            <div className="ad-logo-text">
              <strong>Tradexflow</strong>
              <span>Admin panel</span>
            </div>
            <button
              className="ad-sidebar-close"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close menu"
              style={{
                marginLeft: "auto", display: "none",
                width: 28, height: 28, borderRadius: 7,
                background: "none", border: "1px solid var(--border)",
                color: "var(--text-muted)", cursor: "pointer",
                alignItems: "center", justifyContent: "center",
                flexShrink: 0, transition: "all 0.15s",
              }}
            >
              <X size={13} />
            </button>
          </div>

          {/* Nav */}
          <nav className="ad-nav" onClick={() => setSidebarOpen(false)}>
            {NAV_SECTIONS.map((section) => (
              <div key={section.label}>
                <p className="ad-nav-section-label">{section.label}</p>
                <div className="ad-nav-items">
                  {section.items.map((item) => (
                    <SidebarLink key={item.to} {...item} />
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Admin user card */}
          <div className="ad-user-card">
            <div className="ad-user-inner">
              <div className="ad-user-avatar">{adminInitials}</div>
              <div className="ad-user-info">
                <strong>Super Admin</strong>
                <span>{adminEmail}</span>
              </div>
              <button className="ad-logout-btn" onClick={handleLogout} title="Logout">
                <LogOut size={13} />
              </button>
            </div>
          </div>
        </aside>

        {/* ── Page column ── */}
        <div className="up-page">

          {/* Topbar */}
          <header className="up-topbar">
            <div className="up-topbar-left">
              <button className="up-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="3" width="14" height="1.5" rx="0.75" fill="currentColor"/>
                  <rect x="1" y="7.25" width="14" height="1.5" rx="0.75" fill="currentColor"/>
                  <rect x="1" y="11.5" width="10" height="1.5" rx="0.75" fill="currentColor"/>
                </svg>
              </button>
              <div className="up-topbar-badge">
                <Users size={9} />Users
              </div>
              <div>
                <div className="up-topbar-title">User management</div>
                <div className="up-topbar-sub">Manage accounts, status and activity</div>
              </div>
            </div>
            <div className="up-topbar-right">
              <button className="up-btn" onClick={fetchUsers}>
                <RefreshCw size={12} />Refresh
              </button>
              <button className="up-btn up-btn-primary" onClick={() => setShowAdd(true)}>
                <Plus size={12} />Add user
              </button>
            </div>
          </header>

          {/* Main */}
          <main className="up-main">

            {/* Stat cards */}
            <div className="up-stat-grid">
              {STAT_CARDS.map(({ label, value, icon: Icon, bg, color, glow, bar, sub }) => (
                <div key={label} className="up-stat" style={{ "--stat-glow": glow, "--stat-bar": bar }}>
                  <div className="up-stat-top">
                    <span className="up-stat-label">{label}</span>
                    <div className="up-stat-icon" style={{ background: bg }}>
                      <Icon size={15} style={{ color }} />
                    </div>
                  </div>
                  <div>
                    <div className="up-stat-value">{loading ? "—" : value}</div>
                    <div className="up-stat-sub">{sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Table panel */}
            <div className="up-panel">
              {/* Toolbar */}
              <div className="up-toolbar">
                <div className="up-toolbar-left">
                  <span className="up-panel-title">All users</span>
                  <span className="up-count-pill">{filtered.length}</span>
                </div>
                <div className="up-toolbar-right">
                  <div className="up-filter-tabs">
                    {STATUS_FILTERS.map((s) => (
                      <button
                        key={s}
                        className={`up-filter-tab${statusFilter === s ? " active" : ""}`}
                        onClick={() => { setStatusFilter(s); setPage(1); }}
                      >
                        {s}
                        {s !== "all" && <span className="up-filter-tab-count">{stats[s] ?? 0}</span>}
                      </button>
                    ))}
                  </div>
                  <div className="up-search-wrap">
                    <Search size={12} className="up-search-icon" />
                    <input
                      className="up-search"
                      placeholder="Search name or email…"
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    />
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="up-table-wrap">
                <table className="up-table">
                  <thead>
                    <tr>
                      {[
                        { key: "name",       label: "User"         },
                        { key: "email",      label: "Email"        },
                        { key: "balance",    label: "Balance"      },
                        { key: "plan",       label: "Plan"         },
                        { key: "created_at", label: "Joined"       },
                      ].map(({ key, label }) => (
                        <th key={key} onClick={() => handleSort(key)}>
                          {label}<SortIcon field={key} />
                        </th>
                      ))}
                      <th className="no-sort">Status</th>
                      <th className="no-sort" style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 10 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 5 }).map((_, j) => (
                            <td key={j}><div className="up-skel" style={{ width: `${50 + (j * 17) % 38}%` }} /></td>
                          ))}
                        </tr>
                      ))
                    ) : pageSlice.length === 0 ? (
                      <tr><td colSpan={7}><Empty message="No users match your filters." /></td></tr>
                    ) : pageSlice.map((u) => {
                      const col = avatarColor(u.id);
                      return (
                        <tr key={u.id} onClick={() => setViewUser(u)}>
                          <td>
                            <div className="up-avatar-cell">
                              <div className={`up-avatar up-avatar-${col}`}>{initials(u.first_name, u.last_name)}</div>
                              <div>
                                <div className="up-cell-name">{u.first_name} {u.last_name}</div>
                                <div className="up-cell-uid">{String(u.id).slice(0, 8)}…</div>
                              </div>
                            </div>
                          </td>
                          <td><span className="up-cell-email">{u.email}</span></td>
                          <td>
                            <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: u.wallet_balance > 0 ? "var(--green)" : "var(--text-muted)" }}>
                              {fmtCurrency(u.wallet_balance ?? 0)}
                            </span>
                          </td>
                          <td>
                            {u.subscription_name
                              ? <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: "var(--cyan-dim)", color: "var(--cyan)", border: "1px solid rgba(34,211,238,0.2)" }}>{u.subscription_name}</span>
                              : <span style={{ fontSize: 11, color: "var(--text-muted)" }}>—</span>
                            }
                          </td>
                          <td><span className="up-cell-date">{fmtDate(u.created_at)}</span></td>
                          <td><StatusChip status={u.status} /></td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div className="up-row-actions">
                              <button className="up-icon-btn" title="View" onClick={() => setViewUser(u)}>
                                <Eye size={12} />
                              </button>
                              {u.status !== "active" ? (
                                <button className="up-icon-btn activate" title="Activate"
                                  onClick={async () => {
                                    const { error } = await supabase.from("users").update({ status: "active" }).eq("id", u.id);
                                    if (!error) { handleStatusChange(u.id, "active"); showToast("User activated."); }
                                    else showToast(error.message, false);
                                  }}>
                                  <ShieldCheck size={12} />
                                </button>
                              ) : (
                                <button className="up-icon-btn suspend" title="Suspend"
                                  onClick={async () => {
                                    const { error } = await supabase.from("users").update({ status: "suspended" }).eq("id", u.id);
                                    if (!error) { handleStatusChange(u.id, "suspended"); showToast("User suspended."); }
                                    else showToast(error.message, false);
                                  }}>
                                  <ShieldOff size={12} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="up-pagination">
                  <span className="up-pag-info">
                    {filtered.length
                      ? `${(page - 1) * PER_PAGE + 1}–${Math.min(page * PER_PAGE, filtered.length)} of ${filtered.length}`
                      : "0 users"}
                  </span>
                  <div className="up-pag-pages">
                    {Array.from({ length: Math.min(totalPages, 8) }, (_, i) => i + 1).map((p) => (
                      <button key={p} className={`up-pag-btn${page === p ? " active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </main>
        </div>{/* end .up-page */}
      </div>{/* end .up-shell */}

      {/* Modals */}
      {viewUser && (
        <UserModal
          user={viewUser}
          onClose={() => setViewUser(null)}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          showToast={showToast}
        />
      )}
      {showAdd && (
        <AddUserModal
          onClose={() => setShowAdd(false)}
          onSuccess={(msg) => { showToast(msg); fetchUsers(); }}
        />
      )}

      {/* Toast */}
      {toast.msg && (
        <div className={`up-toast ${toast.ok ? "ok" : "err"}`}>
          {toast.ok
            ? <CheckCircle2 size={14} style={{ color: "var(--green)", flexShrink: 0 }} />
            : <AlertCircle  size={14} style={{ color: "var(--rose)",  flexShrink: 0 }} />}
          {toast.msg}
        </div>
      )}
    </>
  );
}